var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useMemo } from 'react';
import { translate } from '@utils/i18n';
import { AttachmentsSurface } from '@features/Attachments/AttachmentsSurface';
import { buildFileTypeOptions } from '@features/Attachments/utils/filterOptions';
import { InlineNameCell } from '@features/Attachments/components/InlineNameCell';
import { bulkDestroyAttachments, destroyAttachment, fetchInspectionAttachments, updateInspectionAttachment } from './services/inspectionAttachmentsApi';
import { createDirectUploadTransport } from './services/directUploadTransport';
import { InspectionAttachmentsBulkActions } from './components/InspectionAttachmentsBulkActions';
import { useAttachmentBulkEdits } from './hooks/useAttachmentBulkEdits';
import { buildReportOptionsOptions, buildResultOptions } from './utils/filterOptions';
import { toInspectionAttachmentRow } from './utils/toInspectionAttachmentRow';
import { buildInspectionColumns, INSPECTION_SORTABLE_COLUMNS } from './utils/inspectionColumns';
import { INSPECTION_DEFAULT_UPLOAD_ATTRIBUTES, buildAddAttachmentsColumns, buildAddAttachmentsLabels, toInspectionQueueFields } from './utils/addAttachmentsConfig';
import { COMMONS_NS, EDIT_MODAL_ERRORS_NS, INSPECTION_ACCEPT, INSPECTION_ATTACHMENTS_ENTITY_KEY, INSPECTION_ATTACHMENTS_NS, MASS_ACTIONS_NS, PROGRESS_TOAST_NS, UPLOAD_NS } from './constants';
const t = (key, options) => translate(key, Object.assign({ namespace: INSPECTION_ATTACHMENTS_NS }, options));
/**
 * Inspection attachments as a configuration of the shared `AttachmentsSurface`: the table,
 * filters, toolbar, preview, drawer and delete flow all live there. What stays here is what is
 * genuinely Inspection's — the technician, certification-number, result and report-option
 * columns, this surface's endpoints, and its two bulk edits.
 *
 * Deliberately has no `renderExtras`: Building needs one for its add-category drawer, Inspection
 * has no categories at all (the legacy screen has none either).
 */
export function InspectionAttachmentsSurface({ inspectionId, onAddAttachment, maxUploadSizeBytes, hasCoverLetter = false }) {
    const uploadLabels = useMemo(() => buildAddAttachmentsLabels(maxUploadSizeBytes !== null && maxUploadSizeBytes !== void 0 ? maxUploadSizeBytes : DEFAULT_MAX_UPLOAD_SIZE_BYTES), [maxUploadSizeBytes]);
    const config = useMemo(() => ({
        entityKey: INSPECTION_ATTACHMENTS_ENTITY_KEY,
        namespace: INSPECTION_ATTACHMENTS_NS,
        progressToastNamespace: PROGRESS_TOAST_NS,
        className: 'inspection-attachments-table',
        data: {
            fetchAll: fetchInspectionAttachments,
            toRow: toInspectionAttachmentRow,
            destroyOne: destroyAttachment,
            destroyMany: bulkDestroyAttachments
        },
        labels: {
            deleteSuccessToast: (count) => translate('toasts.delete_success', { namespace: MASS_ACTIONS_NS, count }),
            deleteErrorToast: translate('toasts.delete_error', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmBulkTitle: (count) => translate('delete_confirm.bulk_title', { namespace: MASS_ACTIONS_NS, count }),
            deleteConfirmSingleTitle: (fileName) => translate('delete_confirm.single_title', { namespace: MASS_ACTIONS_NS, fileName }),
            deleteConfirmDescription: translate('delete_confirm.description', { namespace: MASS_ACTIONS_NS }),
            deleteConfirmLabel: translate('delete_confirm.confirm_label', { namespace: MASS_ACTIONS_NS }),
            deleteCancelLabel: translate('cancel', { namespace: COMMONS_NS }),
            rowDeleteAriaLabel: (fileName) => translate('row_delete_aria_label', { namespace: MASS_ACTIONS_NS, fileName })
        },
        // The shared cell, not a local copy: its contentEditable revert rules (E2E-27) are subtle
        // enough that duplicating them per surface is how they drift.
        renderNameCell: (row) => (React.createElement(InlineNameCell, { name: row.name, commitName: (nextName) => __awaiter(this, void 0, void 0, function* () {
                try {
                    const updated = yield updateInspectionAttachment(inspectionId, row.id, { name: nextName });
                    return { success: true, name: updated.name };
                }
                catch (_a) {
                    return { success: false, error: translate('generic', { namespace: EDIT_MODAL_ERRORS_NS }) };
                }
            }) })),
        entityColumns: ({ t: surfaceT }) => buildInspectionColumns({ translateFn: surfaceT, inspectionId, hasCoverLetter }),
        entitySortableColumns: INSPECTION_SORTABLE_COLUMNS,
        filters: ({ t: surfaceT }) => [
            {
                key: 'result',
                label: surfaceT('filters.result_label'),
                options: () => buildResultOptions(surfaceT),
                getValues: (row) => [row.resultFilterValue]
            },
            {
                key: 'report_options',
                label: surfaceT('filters.report_options_label'),
                options: () => buildReportOptionsOptions(surfaceT),
                getValues: (row) => row.reportOptionKeys
            },
            {
                key: 'file_type',
                label: surfaceT('filters.file_type_label'),
                options: buildFileTypeOptions,
                getValues: (row) => [row.typeFilterValue]
            }
        ],
        // Search matches the file name, the technician and the certification number — the three
        // things a user would plausibly look a row up by.
        searchableText: (row) => [row.name, row.technicianName, row.certificationNumber],
        renderBulkActions: (ctx) => (React.createElement(InspectionBulkActions, { inspectionId: inspectionId, ctx: ctx, hasCoverLetter: hasCoverLetter })),
        upload: {
            transport: createDirectUploadTransport,
            defaultAttributes: INSPECTION_DEFAULT_UPLOAD_ATTRIBUTES,
            toFields: toInspectionQueueFields,
            columns: ({ updateItemMetadata }) => buildAddAttachmentsColumns(updateItemMetadata),
            labels: uploadLabels,
            // Registry-enforced server-side too (AttachmentType::PDF_ONLY) — this only narrows the
            // file picker and the client-side rejection.
            accept: INSPECTION_ACCEPT,
            successToast: (count) => translate('toasts.success', { namespace: UPLOAD_NS, count })
        }
    }), [inspectionId, uploadLabels, hasCoverLetter]);
    return (React.createElement(AttachmentsSurface, { config: config, ownerId: inspectionId, maxUploadSizeBytes: maxUploadSizeBytes, onAddAttachment: onAddAttachment }));
}
/** Mirrors the surface's own fallback — see `AttachmentsSurface`'s constant of the same name. */
const DEFAULT_MAX_UPLOAD_SIZE_BYTES = 150 * 1024 * 1024;
/**
 * Wraps the bulk-action controls with their mutations. A component rather than inline JSX because
 * `useAttachmentBulkEdits` is a hook and `renderBulkActions` is called during render.
 */
function InspectionBulkActions({ inspectionId, ctx, hasCoverLetter }) {
    const bulkEdits = useAttachmentBulkEdits({
        inspectionId,
        selectedIds: ctx.selectedIds,
        clearSelection: ctx.clearSelection,
        invalidate: ctx.invalidate
    });
    // Indeterminate checkbox state: mixed values across the selected rows, same treatment Building's
    // visibility popover gives its two flags.
    const summaryValues = ctx.selectedRows.map((row) => row.showOnReport);
    const summaryChecked = summaryValues.length > 0 && summaryValues.every(Boolean);
    const summaryIndeterminate = summaryValues.length > 0 && !summaryChecked && summaryValues.some(Boolean);
    const coverLetterValues = ctx.selectedRows.map((row) => row.includeInCoverLetter);
    const coverLetterChecked = coverLetterValues.length > 0 && coverLetterValues.every(Boolean);
    const coverLetterIndeterminate = coverLetterValues.length > 0 && !coverLetterChecked && coverLetterValues.some(Boolean);
    return (React.createElement(InspectionAttachmentsBulkActions, { updateResultLabel: t('toolbar.update_result'), updateReportOptionsLabel: t('toolbar.update_report_options'), resultOptions: buildResultOptions(t), summaryCheckboxLabel: t('report_options.summary_label'), summaryChecked: summaryChecked, summaryIndeterminate: summaryIndeterminate, showCoverLetterAction: hasCoverLetter, coverLetterCheckboxLabel: t('report_options.cover_letter_label'), coverLetterChecked: coverLetterChecked, coverLetterIndeterminate: coverLetterIndeterminate, disabled: ctx.selectedIds.length === 0 || ctx.isMutating || bulkEdits.isMutating, onUpdateResult: bulkEdits.updateResult, onUpdateReportOptions: bulkEdits.updateReportOptions }));
}
